import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState } from "./common"

/**
 * 変換レートの整数表現。
 * 変換率 = conversion_rate / conversion_rate_unit
 * 例: 44.01% → conversion_rate=4401, conversion_rate_unit=10000
 */
export const ConversionRateValue = z.object({
  conversion_rate: z.number().int().describe("変換レートの分子（整数）。例: 4401 = 44.01%"),
  conversion_rate_unit: z.number().int().describe("変換レートの分母（固定値 10000）"),
})

export type ConversionRateValue = z.infer<typeof ConversionRateValue>

/**
 * 標準レートの単一バージョン。
 * rates 配列の要素。
 */
export const StandardConversionRateEntry = z.intersection(
  ConversionRateValue,
  z.object({
    effective_period: Period,
    effective_period_state: PeriodState,
  })
)

export type StandardConversionRateEntry = z.infer<typeof StandardConversionRateEntry>

/**
 * app-configs/conversion-rate ドキュメント本体。
 * from_power: パワー→LIVEポイント 変換レート
 * to_free_coin: LIVEポイント→無償コイン交換レート
 * to_expiring_free_coin: 無償パワー→期限付き無償コイン変換レート
 */
export const AppConfigConversionRateDocumentBody = z.object({
  from_power: z.object({
    rates: z.array(StandardConversionRateEntry).describe("パワー→LIVEポイント 標準レートのバージョン一覧"),
  }),
  to_free_coin: z.object({
    rates: z.array(StandardConversionRateEntry).describe("LIVEポイント→無償コイン 標準レートのバージョン一覧"),
  }),
  to_expiring_free_coin: z.object({
    rates: z.array(StandardConversionRateEntry).describe("無償パワー→期限付き無償コイン 標準レートのバージョン一覧"),
  }),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type AppConfigConversionRateDocumentBody = z.infer<typeof AppConfigConversionRateDocumentBody>

/**
 * 変換レートの固定分母。% 表記で小数第二位まで対応（最小単位 0.01%）。
 */
export const CONVERSION_RATE_UNIT = 10000

/**
 * 標準レート設定リクエスト（管理API用）。
 * from_power / to_free_coin / to_expiring_free_coin それぞれ個別に更新可能。
 */
const ConversionRateEntryRequest = z.object({
  conversionRate: z.number().int().min(0).describe("変換レートの分子（整数）。例: 4401 = 44.01%"),
  period: z.object({
    startAt: z.number().describe("有効期間開始日時 (UNIX Timestamp millis)"),
    endAt: z.number().nullable().describe("有効期間終了日時 (UNIX Timestamp millis、nullの場合は無期限)"),
  }),
}).refine(
  (data) => data.period.endAt === null || data.period.startAt < data.period.endAt,
  {
    message: "開始日時は終了日時より前である必要があります",
    path: ["period", "endAt"],
  }
)

const hasIndefiniteRate = (rates: { period: { endAt: number | null } }[]) =>
  rates.some((r) => r.period.endAt === null)

export const SetConversionRateRequest = z.object({
  fromPower: z.object({
    rates: z.array(ConversionRateEntryRequest).min(1, "rates は1つ以上必要です"),
  }).optional().describe("パワー→LIVEポイント 標準レート設定。省略時は既存値を維持"),
  toFreeCoin: z.object({
    rates: z.array(ConversionRateEntryRequest).min(1, "rates は1つ以上必要です"),
  }).optional().describe("LIVEポイント→無償コイン 標準レート設定。省略時は既存値を維持"),
  toExpiringFreeCoin: z.object({
    rates: z.array(ConversionRateEntryRequest).min(1, "rates は1つ以上必要です"),
  }).optional().describe("無償パワー→期限付き無償コイン 標準レート設定。省略時は既存値を維持"),
  updatedAt: z.number().int().optional().describe("編集元データの最終更新日時（楽観的ロック用）。新規作成時は省略可"),
}).refine(
  (data) => !data.fromPower || hasIndefiniteRate(data.fromPower.rates),
  { message: "無期限（endAt: null）のレートが最低1件必要です", path: ["fromPower", "rates"] },
).refine(
  (data) => !data.toFreeCoin || hasIndefiniteRate(data.toFreeCoin.rates),
  { message: "無期限（endAt: null）のレートが最低1件必要です", path: ["toFreeCoin", "rates"] },
).refine(
  (data) => !data.toExpiringFreeCoin || hasIndefiniteRate(data.toExpiringFreeCoin.rates),
  { message: "無期限（endAt: null）のレートが最低1件必要です", path: ["toExpiringFreeCoin", "rates"] },
)

export type SetConversionRateRequest = z.infer<typeof SetConversionRateRequest>

/**
 * ユーザー個別レートの1セクション（パワー→LIVEポイント / LIVEポイント→無償コイン 等）。
 * conversion_rate / conversion_rate_unit に加えて effective_from を持つ。
 */
export const UserLivePointRateEntry = z.intersection(
  ConversionRateValue,
  z.object({
    effective_from: z.number().describe("有効開始日時 (UNIX Timestamp millis)。この時刻以降、標準レートの代わりにこのレートが適用される"),
  })
)

export type UserLivePointRateEntry = z.infer<typeof UserLivePointRateEntry>

/**
 * ユーザー個別レートのオーバーライド。
 * user-privates/{userId} ドキュメントの live_point_rate プロパティとして格納する。
 * 現在は from_power（パワー→LIVEポイント変換レート）のみ対応。
 */
export const UserLivePointRateOverride = z.object({
  from_power: UserLivePointRateEntry.optional().describe("パワー→LIVEポイント 個別変換レート"),
})

export type UserLivePointRateOverride = z.infer<typeof UserLivePointRateOverride>

/**
 * ユーザー個別レート設定リクエスト（管理API用）。
 * 現在は fromPower（パワー→LIVEポイント変換レート）のみ対応。
 */
export const SetUserLivePointRateRequest = z.object({
  fromPower: z.object({
    conversionRate: z.number().int().min(0).describe("変換レートの分子（整数）。例: 4401 = 44.01%"),
    effectiveFrom: z.number().describe("有効開始日時 (UNIX Timestamp millis)"),
  }).optional().describe("パワー→LIVEポイント 個別レート設定。省略時は既存値を維持"),
})

export type SetUserLivePointRateRequest = z.infer<typeof SetUserLivePointRateRequest>
